import {EHelpIndexRole, HELP, helpId, IHelpDb, IHelpFrag, IHelpIndex, IHelpIndexNode, IHelpSearchResults, OHelpSearchExp} from "back/help/helpApi";
import {IReg} from "lib/commons/registry";
import {LANG} from "lib/commons/lang";

export class HelpUnionDb implements IHelpDb {
	constructor(public reg: IReg<unknown>, public baseDb: IHelpDb | undefined, public subDbs: Map<string, { db: IHelpDb, title: string }>) {}

	initIfNeeded(): Promise<this> | this {
		let dbInits: Promise<IHelpDb>[] = [];
		LANG.pushIfDefined(dbInits, this.baseDb?.initIfNeeded())
		this.subDbs.forEach(v => LANG.pushIfDefined(dbInits, v.db.initIfNeeded()));
		return dbInits.length > 0 ? Promise.all(dbInits).then(() => this) : this;
	}

	getFragment(id: helpId | null): Promise<IHelpFrag | undefined> {
		if (!id) return undefined;
		const sep = id.indexOf(HELP.UNIONDB_SEP_ID);
		if (sep < 0) {
			return this.baseDb.getFragment(id);
		} else {
			return this.subDbs.get(id.substring(0, sep))?.db.getFragment(id);
		}
	}

	async search(crit: OHelpSearchExp): Promise<IHelpSearchResults> {
		let results = await this.baseDb.search(crit);
		for (const dbHolder of this.subDbs.values()) {
			results.push(...await dbHolder.db.search(crit));
		}
		results.sort((r1, r2) => r2.score - r1.score);
		return results;
	}

	async getIndex(key: string): Promise<IHelpIndex | undefined> {
		const found: IHelpIndex[] = [];
		LANG.pushIfDefined(found, this.baseDb ? await this.baseDb.getIndex(key) : undefined)
		for (const dbHolder of this.subDbs.values()) {
			LANG.pushIfDefined(found, await dbHolder.db.getIndex(key));
		}
		if (found.length === 0) return undefined;
		if (found.length === 1) return found[0];
		const members: (IHelpFrag | IHelpIndexNode)[] = [];
		for (const index of found) members.push(...await index.getMembers());
		return new UnionHelpIndex(key, found[0].indexRole, members);
	}

	async getKeyIndexes(role: EHelpIndexRole): Promise<Set<string>> {
		let indexes = this.baseDb ? await this.baseDb.getKeyIndexes(role) : new Set<string>();
		for (const dbHolder of this.subDbs.values()) {
			for (const key of await dbHolder.db.getKeyIndexes(role)) indexes.add(key);
		}
		return indexes;
	}

	async getIndexesFor(frag: IHelpFrag): Promise<(IHelpIndex | IHelpIndexNode)[]> {
		let indexes = await this.baseDb.getIndexesFor(frag);
		for (const dbHolder of this.subDbs.values()) {
			indexes.push(...await dbHolder.db.getIndexesFor(frag));
		}
		return indexes;
	}

	async getIndexesWithRoleFor(frag: IHelpFrag, indexRole: EHelpIndexRole): Promise<(IHelpIndex | IHelpIndexNode)[]> {
		let indexes = await this.baseDb.getIndexesWithRoleFor(frag, indexRole);
		for (const dbHolder of this.subDbs.values()) {
			indexes.push(...await dbHolder.db.getIndexesWithRoleFor(frag, indexRole));
		}
		return indexes;
	}
}

class UnionHelpIndex implements IHelpIndex {

	constructor(public indexKey: string, public indexRole: EHelpIndexRole, protected members: (IHelpFrag | IHelpIndexNode)[]) {}

	async getMembers(): Promise<(IHelpFrag | IHelpIndexNode)[]> {return this.members}

	isTreeNode(): this is IHelpIndexNode {return false}
}